import { read, utils, writeFile } from 'xlsx';
import ExcelJS from 'exceljs';
import { translations } from '../i18n/translations';

import { validateCable } from './validation';
import { formatSezione, getCableDisplayName, getTerminalData } from './cableLogic';
import { generateACPowerData } from './acPowerLogic';
// import { getSortedCablesForList } from '../components/visualization/UnifiedCablesList'; // This will be passed as a prop
import { COLOR_MAP } from '../constants/colorConstants'; // Changed from CABLE_COLORS_MAP to COLOR_MAP

// ==================== IMPORT FUNCTIONS ====================

const importSettings = (worksheet, setSettings, setRevision, setSchemaElettrico, setInstallazione, setFileName, setGroupTitles, setListSortMode) => {
  const settingsMap = {
    'B2': 'quantitaCablaggi',
    'B3': 'marcaCavi',
    'B4': 'grandezzaGrassetto',
    'B5': 'posMarcaturaTesta',
    'B6': 'posMarcaturaCoda',
    'B7': 'ribaltaTesta',
    'B8': 'ribaltaCentrale',
    'B9': 'ribaltaCoda',
    'B10': 'loopType',
    'B11': 'loopNum',
    'B12': 'loopDist',
    'B13': 'lingua',
    'B14': 'ordinamentoLista',
    'B15': 'abilitaMarcature2',
    'B16': 'tipoMarcature2',
    'B17': 'distanzaMarcature2',
    'B18': 'revision',
    'B19': 'schemaElettrico',
    'B20': 'installazione',
    'B21': 'marcaturePerCollegamento',
    'B22': 'fileName',
    'B23': 'useAWG',
    'B24': 'forceBlackCables',
    'B25': 'reducePeelForManualMarking'
  };

  const importedSettings = {};
  Object.entries(settingsMap).forEach(([cell, key]) => {
    if (worksheet[cell]) {
      // CAMPI STRINGA - LISTA ESPLICITA
      if (['lingua', 'ordinamentoLista', 'tipoMarcature2', 'schemaElettrico', 'installazione', 'fileName'].includes(key)) {
        importedSettings[key] = String(worksheet[cell].v || '');
      }
      // CAMPO BOOLEAN
      else if (['abilitaMarcature2', 'marcaturePerCollegamento', 'useAWG', 'forceBlackCables', 'reducePeelForManualMarking'].includes(key)) {
        importedSettings[key] = Boolean(worksheet[cell].v);
      }
      // CAMPO REVISION (numero speciale)
      else if (key === 'revision') {
        importedSettings[key] = parseInt(worksheet[cell].v) || 0;
      }
      // TUTTI GLI ALTRI CAMPI NUMERICI
      else {
        importedSettings[key] = parseInt(worksheet[cell].v) || 0;
      }
    }
  });

  setSettings(prev => ({ ...prev, ...importedSettings }));

  if (importedSettings.revision !== undefined) setRevision(Math.max(0, Math.min(99, parseInt(importedSettings.revision) || 0)));
  if (importedSettings.schemaElettrico !== undefined) setSchemaElettrico(importedSettings.schemaElettrico);
  if (importedSettings.installazione !== undefined) setInstallazione(importedSettings.installazione);
  if (importedSettings.fileName !== undefined) setFileName(importedSettings.fileName);
  if (importedSettings.ordinamentoLista) {
    setListSortMode(importedSettings.ordinamentoLista);
  }
};

const importWiringTableData = (worksheet, availableSheaths = []) => {
  const normalizeHeader = (header) => String(header || '').replace(/\./g, '').trim().toLowerCase();

  const headerMap = {
    n: 'N', gruppo: 'Gruppo', siglatura: 'Siglatura', colore: 'Colore',
    'sez [mmq]': 'Sez [mmq]', 'sez mmq': 'Sez [mmq]',
    'lung [mm]': 'Lung [mm]', 'lung mm': 'Lung [mm]',
    'term a': 'TERM A', 'term b': 'TERM B',
    'colleg a': 'COLLEG A', 'colleg b': 'COLLEG B',
    'guaina': 'Guaina'
  };

  const raw = utils.sheet_to_json(worksheet, { header: 1 });
  if (raw.length === 0) return [];

  const headers = raw[0].map(h => normalizeHeader(h));
  const mappedHeaders = headers.map(h => headerMap[h] || h);

  const jsonData = raw.slice(1).map(row => {
    const obj = {};
    mappedHeaders.forEach((key, idx) => {
      obj[key] = row[idx];
    });
    return obj;
  }).filter(row => {
    let numValue = row['N'];
    if (typeof numValue === 'string' && numValue.startsWith('#')) {
      numValue = numValue.substring(1);
    }
    const hasValidNumber = numValue && !isNaN(parseInt(numValue));
    const hasAnyContent = Object.values(row).some(value =>
      value !== undefined && value !== null && String(value).trim() !== ''
    );
    const hasTerminals = row['TERM A'] && row['TERM B'];

    return hasValidNumber && hasAnyContent && hasTerminals;
  });

  return jsonData.map(row => {
    let numValue = row['N'];
    if (typeof numValue === 'string' && numValue.startsWith('#')) {
      numValue = numValue.substring(1);
    }
    
    // Gestione guaina: #1 → ID della prima guaina nell'array
    let sheathId = null;
    if (row['Guaina'] && availableSheaths.length > 0) {
      const sheathRef = String(row['Guaina']).trim();
      if (sheathRef.startsWith('#')) {
        const sheathIndex = parseInt(sheathRef.substring(1)) - 1;
        if (sheathIndex >= 0 && sheathIndex < availableSheaths.length) {
          sheathId = availableSheaths[sheathIndex].id;
        }
      }
    }
    
    return {
      originalIndex: parseInt(numValue) - 1,
      gruppo: row['Gruppo'] || '',
      siglatura: row['Siglatura'] || '',
      colore: row['Colore'] || 'ARANCIO',
      sezione: parseFloat(row['Sez [mmq]']) || 0.5,
      lunghezza: parseInt(row['Lung [mm]']) || 200,
      terminaleA: row['TERM A'] || 'PSW',
      terminaleB: row['TERM B'] || 'PSW',
      collegamentoA: row['COLLEG A'] || '',
      collegamentoB: row['COLLEG B'] || '',
      guaina: sheathId,
      type: 'standard'
    };
  });
};

const importMultipolarCablesData = (worksheet) => {
  try {
    const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
    if (jsonData.length < 2) return [];

    const rows = jsonData.slice(1).filter(row =>
      row.some(cell => cell !== undefined && cell !== null && String(cell).trim() !== '')
    );
    if (rows.length === 0) return [];

    const multipolarCables = [];
    let currentCable = null;

    rows.forEach(row => {
      const numeroCell = row[0];
      const isNewCable = numeroCell && String(numeroCell).startsWith('#');

      if (isNewCable) {
        if (currentCable && currentCable.poli.length > 0) {
          multipolarCables.push({ ...currentCable, type: 'multipolar' });
        }
        const originalIndex = parseInt(String(numeroCell).substring(1)) - 1;
        currentCable = {
          originalIndex: originalIndex,
          nomeCavo: row[1] || '',
          gruppo: row[2] || '',
          lunghezza: parseInt(row[3]) || 200,
          poli: []
        };
      }

      if (currentCable && row[5]) {
        const polo = {
          id: Date.now() + Math.random(),
          colore: row[6] || 'ARANCIO',
          sezione: parseFloat(row[7]) || 0.5,
          siglaturaA: row[8] || '',
          siglaturaB: row[9] || '',
          lunghezzaA: row[10] !== undefined && row[10] !== null && row[10] !== '' ? parseInt(row[10]) : 0,
          lunghezzaB: row[11] !== undefined && row[11] !== null && row[11] !== '' ? parseInt(row[11]) : 0,
          terminaleA: row[12] || 'PSW',
          terminaleB: row[13] || 'PSW',
          collegamentoA: row[14] || '',
          collegamentoB: row[15] || ''
        };
        currentCable.poli.push(polo);
      }
    });

    if (currentCable && currentCable.poli.length > 0) {
      multipolarCables.push({ ...currentCable, type: 'multipolar' });
    }
    return multipolarCables;
  } catch (error) {
    console.error('Errore nell\'importazione cavi multipolari:', error);
    return [];
  }
};

const importGroupTitles = (worksheet, setGroupTitles) => {
  try {
    const jsonData = utils.sheet_to_json(worksheet, {
      header: ['GRUPPO', 'TITOLO', 'NUM_CAVI'],
      range: 1 // Salta la prima riga (header)
    });

    const importedTitles = {};
    jsonData.forEach((row) => {
      if (row.GRUPPO && row.TITOLO && String(row.TITOLO).trim() !== '') {
        importedTitles[row.GRUPPO] = row.TITOLO;
      }
    });

    if (Object.keys(importedTitles).length > 0) {
      setGroupTitles(importedTitles);
    }
  } catch (error) {
    console.error('❌ Errore nell\'importazione Group Titles:', error);
  }
};

const importSheathsData = (worksheet) => {
  try {
    const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
    if (jsonData.length < 2) return [];

    const sheaths = [];
    jsonData.slice(1).forEach((row, index) => {
      if (row[0] || row[1] || row[2] || row[3]) {
        sheaths.push({
          id: Date.now() + index, // ID temporaneo (verrà usato l'indice)
          type: row[1] || 'PVC',
          diameter: parseFloat(row[2]) || 6,
          length: parseInt(row[3]) || 300,
          label: `Ø${row[2] || 6} - L${row[3] || 300}`,
          fullLabel: `Guaina ${row[1] || 'PVC'} Ø${row[2] || 6} - L${row[3] || 300}`
        });
      }
    });

    return sheaths;
  } catch (error) {
    console.error('❌ Errore nell\'importazione guaine:', error);
    return [];
  }
};


export const handleFileImport = (event, setCables, setWarnings, setSettings, setRevision, setSchemaElettrico, setInstallazione, setFileName, setGroupTitles, setListSortMode, setSuccessMessage, setAvailableSheaths) => {
  const file = event.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = (e) => {
    try {
      const data = new Uint8Array(e.target.result);
      const workbook = read(data, { type: 'array' });
      let allImportedCables = [];
      let importedSheaths = [];

      // PRIMA: Importa le guaine se presenti
      if (workbook.SheetNames.includes('Sheaths')) {
        importedSheaths = importSheathsData(workbook.Sheets['Sheaths']);
        if (importedSheaths.length > 0 && setAvailableSheaths) {
          setAvailableSheaths(importedSheaths);
        }
      }

      if (workbook.SheetNames.includes('Settings')) {
        importSettings(workbook.Sheets['Settings'], setSettings, setRevision, setSchemaElettrico, setInstallazione, setFileName, setGroupTitles, setListSortMode);
      }

      if (workbook.SheetNames.includes('Wiring Table')) {
        const standardCables = importWiringTableData(workbook.Sheets['Wiring Table'], importedSheaths);
        allImportedCables.push(...standardCables);
      }

      if (workbook.SheetNames.includes('Multipolar Cables')) {
        const multipolarCables = importMultipolarCablesData(workbook.Sheets['Multipolar Cables']);
        allImportedCables.push(...multipolarCables);
      }

      if (allImportedCables.length > 0) {
        const sortedCables = allImportedCables
          .sort((a, b) => (a.originalIndex || 0) - (b.originalIndex || 0))
          .map((cable, index) => ({ ...cable, originalIndex: index }));
        setCables(sortedCables);
      }

      if (workbook.SheetNames.includes('Group Titles')) {
        importGroupTitles(workbook.Sheets['Group Titles'], setGroupTitles);
      }

      // Funzione di traduzione (usa 'it' come default poiché settings non è disponibile qui)
      const language = 'it';
      const t = (key, params = {}) => {
        const keys = key.split('.');
        let text = translations[language];
        for (const k of keys) {
          if (text && typeof text === 'object' && k in text) {
            text = text[k];
          } else {
            return key;
          }
        }
        if (typeof text === 'string' && params) {
          return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
        }
        return text || key;
      };

      if (typeof setSuccessMessage === 'function') {
        setSuccessMessage(t('fileOperations.fileImportedSuccess', { name: file.name }));
        setTimeout(() => setSuccessMessage(''), 5000);
      } else {
        setWarnings(prev => [...prev, t('fileOperations.fileImportedSuccess', { name: file.name })]);
      }
      event.target.value = null;

    } catch (error) {
      console.error("Errore durante l'importazione del file Excel:", error);
      const language = 'it';
      const t = (key, params = {}) => {
        const keys = key.split('.');
        let text = translations[language];
        for (const k of keys) {
          if (text && typeof text === 'object' && k in text) {
            text = text[k];
          } else {
            return key;
          }
        }
        if (typeof text === 'string' && params) {
          return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
        }
        return text || key;
      };
      setWarnings(prev => [...prev, t('fileOperations.importError', { error: error.message })]);
    }
  };
  reader.readAsArrayBuffer(file);
};

// ==================== EXPORT FUNCTIONS ====================

const buildFullFileName = (settings, exportForEW3035, extension) => {
  const rawName = settings.fileName || '';
  const baseName = rawName.startsWith('ELECB') ? rawName : `ELECB${rawName}`;
  const withoutExtension = baseName.replace(/\.(xlsx?|pdf)$/i, '');

  const parts = [withoutExtension];

  const revisionNumber = Number.isFinite(settings.revision) ? settings.revision : parseInt(settings.revision) || 0;
  const revisionFormatted = String(revisionNumber).padStart(2, '0');
  parts.push(exportForEW3035 ? `Rev${revisionFormatted}` : `Rev. ${revisionFormatted}`);

  if (settings.listSortMode && settings.listSortMode !== 'numerazione' && !exportForEW3035) {
    parts.push(`[${settings.listSortMode}]`);
  }

  if ((settings.quantitaCablaggi || 1) > 1 || exportForEW3035) {
    const qty = settings.quantitaCablaggi || 1;
    parts.push(exportForEW3035 ? `Q${qty}` : `#${qty}`);
  }

  const separator = exportForEW3035 ? '-' : ' - ';
  const fileName = parts.join(separator);
  return `${fileName}.${extension}`;
};

export const exportToXLSX = async (cables, settings, isSelectionModeEnabled, selectedCables, setWarnings, setSuccessMessage, getSortedCablesForList, getGroupTitle, getCableDisplayName, formatSezione, getTerminalData, exportForEW3035, terminali, availableSheaths = []) => {
  const language = settings?.lingua || 'it';
  const t = (key, params = {}) => {
    const keys = key.split('.');
    let text = translations[language];
    for (const k of keys) {
      if (text && typeof text === 'object' && k in text) {
        text = text[k];
      } else {
        return key;
      }
    }
    if (typeof text === 'string' && params) {
      return text.replace(/\{(\w+)\}/g, (match, key) => params[key] || match);
    }
    return text || key;
  };

  const fileName = settings.fileName;

  if (!fileName || fileName.trim() === '') {
    setWarnings(prev => [
      ...prev,
      t('fileOperations.noFileNameWarning')
    ]);
    return;
  }

  let cablesToExport = cables;
  if (isSelectionModeEnabled && selectedCables.size > 0) {
    cablesToExport = cables.filter((_, index) => selectedCables.has(index));

    if (cablesToExport.length === 0) {
      setWarnings(prev => [
        ...prev,
        t('fileOperations.noCablesSelectedExport')
      ]);
      return;
    }
  }

  try {
    const workbook = new ExcelJS.Workbook();

    await createWiringTableSheet(workbook, cablesToExport, settings, getSortedCablesForList, getCableDisplayName, getTerminalData, availableSheaths);
    await createMultipolarCablesSheet(workbook, cablesToExport, settings, getSortedCablesForList, getGroupTitle, getCableDisplayName, getTerminalData);
    await createSheathsSheet(workbook, availableSheaths);

    const hasGroups = cablesToExport.some(cable => cable.gruppo && cable.gruppo.trim() !== '');
    const hasGroupTitles = settings.groupTitles && Object.keys(settings.groupTitles).length > 0;
    
    if (hasGroups || hasGroupTitles) {
      await createGroupTitlesSheet(workbook, cablesToExport, getGroupTitle);
    }
    
    await createSettingsSheet(workbook, settings, fileName, getGroupTitle);
    await createACPowerSheet(workbook, cablesToExport, settings, getSortedCablesForList, getCableDisplayName, getTerminalData, exportForEW3035, terminali, setWarnings);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const fullFileName = buildFullFileName(settings, exportForEW3035, 'xlsx');
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fullFileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Messaggio di successo specifico per esportazione
    const totalCables = cables.length;
    const exportedCables = cablesToExport.length;
    
    if (isSelectionModeEnabled && selectedCables.size > 0) {
      setSuccessMessage(t('fileOperations.fileExportedSuccessSelected', { name: fullFileName, exported: exportedCables, total: totalCables }));
    } else {
      setSuccessMessage(t('fileOperations.fileExportedSuccessTotal', { name: fullFileName, exported: exportedCables }));
    }
    setTimeout(() => setSuccessMessage(''), 5000);
  } catch (error) {
    console.error('Errore durante l\'esportazione:', error);
    setWarnings(prev => [
      ...prev,
      t('fileOperations.exportError', { error: error.message || error.toString() })
    ]);
  }
};

const createSettingsSheet = async (workbook, settings, fileName, getGroupTitle) => {
  const sheet = workbook.addWorksheet('Settings');
  
  // STRUTTURA IDENTICA ALL'ORIGINALE: Header + dati
  const settingsData = [
    ['Parametro', 'Valore'],
    ['Quantità Cablaggi', settings.quantitaCablaggi],
    ['Marcare Cavi', settings.marcaCavi],
    ['Grandezza Grassetto', settings.grandezzaGrassetto],
    ['Pos. Marcatura Testa', settings.posMarcaturaTesta],
    ['Pos. Marcatura Coda', settings.posMarcaturaCoda],
    ['Ribalta Testa', settings.ribaltaTesta],
    ['Ribalta Centrale', settings.ribaltaCentrale],
    ['Ribalta Coda', settings.ribaltaCoda],
    ['Loop Type', settings.loopType],
    ['Loop Num', settings.loopNum],
    ['Loop Dist', settings.loopDist],
    ['Lingua', settings.lingua],
    ['Ordinamento Lista', settings.listSortMode],
    ['Abilita Marcature 2', settings.abilitaMarcature2],
    ['Tipo Marcature 2', settings.tipoMarcature2],
    ['Distanza Marcature 2', settings.distanzaMarcature2],
    ['Revision', settings.revision],
    ['Schema Elettrico', settings.schemaElettrico],
    ['Installazione', settings.installazione],
    ['Marcature Per Collegamento', settings.marcaturePerCollegamento],
    ['File Name', fileName],
    ['Use AWG', settings.useAWG],
    ['Force Black Cables', settings.forceBlackCables],
    ['Reduce Peel For Manual Marking', settings.reducePeelForManualMarking]
  ];

  settingsData.forEach(row => sheet.addRow(row));

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  sheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
  });

  sheet.columns.forEach(col => col.width = 22);
};

const createSheathsSheet = async (workbook, availableSheaths) => {
  if (!availableSheaths || availableSheaths.length === 0) {
    return; // Non crea lo sheet se non ci sono guaine
  }

  const sheet = workbook.addWorksheet('Sheaths');
  const headers = ['N.', 'Tipo', 'Diametro [mm]', 'Lunghezza [mm]'];

  sheet.addRow(headers);
  sheet.getRow(1).height = 35;

  availableSheaths.forEach((sheath, index) => {
    sheet.addRow([
      `#${index + 1}`,
      sheath.type || 'PVC',
      sheath.diameter,
      sheath.length
    ]);
  });

  sheet.columns = [
    { width: 6 },      // N.
    { width: 12 },     // Tipo
    { width: 15 },     // Diametro
    { width: 15 }      // Lunghezza
  ];

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF87CEEB' } // Azzurro/sky
    };
  });

  sheet.eachRow((row) => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
    });
  });
};

const createWiringTableSheet = async (workbook, cablesToExport, settings, getSortedCablesForList, getCableDisplayName, getTerminalData, availableSheaths = []) => {
  const sheet = workbook.addWorksheet('Wiring Table');
  
  // Mostra colonna "Guaina" SOLO se ci sono guaine
  const hasSheaths = availableSheaths.length > 0;
  const headers = [
    'N.', 'Gruppo', 'Siglatura', 'Colore', 'Sez. [mmq]', 'Lung. [mm]',
    'TERM. A', 'TERM. B', 'COLLEG. A', 'COLLEG. B'
  ];
  if (hasSheaths) {
    headers.push('Guaina');
  }

  sheet.addRow(headers);
  sheet.getRow(1).height = 35;

  const standardCables = getSortedCablesForList(cablesToExport).filter(cable => !(cable.poli && Array.isArray(cable.poli)));

  standardCables.forEach((cable) => {
    const row = [
      `#${cable.originalIndex + 1}`,
      cable.gruppo || '',
      cable.siglatura || '',
      cable.colore || 'ARANCIO',
      cable.sezione,
      cable.lunghezza,
      cable.terminaleA || 'PSW',
      cable.terminaleB || 'PSW',
      cable.collegamentoA || '',
      cable.collegamentoB || ''
    ];
    
    // Aggiungi guaina solo se ci sono guaine
    if (hasSheaths) {
      const sheathIndex = cable.guaina ? availableSheaths.findIndex(s => s.id === cable.guaina) + 1 : '';
      row.push(sheathIndex ? `#${sheathIndex}` : '');
    }
    
    sheet.addRow(row);
  });

  const columns = [
    { width: 4.71 },   // N.
    { width: 10.71 },  // Gruppo
    { width: 10.71 },  // Siglatura
    { width: 15.71 },  // Colore
    { width: 12.71 },  // Sez
    { width: 12.71 },  // Lung
    { width: 10.71 },  // TERM A
    { width: 10.71 },  // TERM B
    { width: 15.71 },  // COLLEG A
    { width: 15.71 }   // COLLEG B
  ];
  if (hasSheaths) {
    columns.push({ width: 10.71 }); // Guaina
  }
  sheet.columns = columns;

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDBEAFE' }
    };
  });

  sheet.eachRow((row) => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
    });
  });

  const lastRowWT = sheet.lastRow?.number || 1;
  for (let row = 2; row <= lastRowWT; row++) {
    const cell = sheet.getRow(row).getCell(1);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFFFF59E' }
    };
  }
};

const createMultipolarCablesSheet = async (workbook, cablesToExport, settings, getSortedCablesForList, getGroupTitle, getCableDisplayName, getTerminalData) => {
  const multipolarCables = getSortedCablesForList(cablesToExport).filter(cable => cable.poli && Array.isArray(cable.poli));

  if (multipolarCables.length === 0) {
    return; // Non crea lo sheet se non ci sono cavi multipolari
  }

  const sheet = workbook.addWorksheet('Multipolar Cables');
  const headers = [
    'N.', 'Nome Cavo', 'Gruppo', 'Lung. Base [mm]', 'N° Poli',
    'Polo', 'Colore', 'Sez. [mmq]', 'Sigla A', 'Sigla B',
    'Lung. A [mm]', 'Lung. B [mm]', 'TERM. A', 'TERM. B',
    'COLLEG. A', 'COLLEG. B'
  ];

  sheet.addRow(headers);
  sheet.getRow(1).height = 35;
  let currentRowMP = 2;
  const sortedMulti = multipolarCables.sort((a, b) => (a.originalIndex || 0) - (b.originalIndex || 0));
  sortedMulti.forEach((cable) => {
    const startRow = currentRowMP;
    const endRow = currentRowMP + cable.poli.length - 1;
    cable.poli.forEach((polo, poloIndex) => {
      const isFirst = poloIndex === 0;
      sheet.addRow([
        isFirst ? `#${cable.originalIndex + 1}` : '',
        isFirst ? (cable.nomeCavo || '') : '',
        isFirst ? (cable.gruppo || '') : '',
        isFirst ? cable.lunghezza : '',
        isFirst ? cable.poli.length : '',
        `P${poloIndex + 1}`,
        polo.colore || 'ARANCIO',
        polo.sezione,
        polo.siglaturaA || '',
        polo.siglaturaB || '',
        polo.lunghezzaA,
        polo.lunghezzaB,
        polo.terminaleA || 'PSW',
        polo.terminaleB || 'PSW',
        polo.collegamentoA || '',
        polo.collegamentoB || ''
      ]);
      currentRowMP++;
    });

    if (cable.poli.length > 1) {
      sheet.mergeCells(`A${startRow}:A${endRow}`);
      sheet.mergeCells(`B${startRow}:B${endRow}`);
      sheet.mergeCells(`C${startRow}:C${endRow}`);
      sheet.mergeCells(`D${startRow}:D${endRow}`);
      sheet.mergeCells(`E${startRow}:E${endRow}`);
      ['A', 'B', 'C', 'D', 'E'].forEach(col => {
        const cell = sheet.getCell(`${col}${startRow}`);
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      });
    }
  });

  sheet.columns = [
    { width: 6 },   // N.
    { width: 15 },  // Nome Cavo
    { width: 10 },  // Gruppo
    { width: 12 },  // Lung. Base
    { width: 8 },   // N° Poli
    { width: 6 },   // Polo
    { width: 12 },  // Colore
    { width: 10 },  // Sez
    { width: 10 },  // Sigla A
    { width: 10 },  // Sigla B
    { width: 10 },  // Lung. A
    { width: 10 },  // Lung. B
    { width: 10 },  // TERM. A
    { width: 10 },  // TERM. B
    { width: 15 },  // COLLEG. A
    { width: 15 }   // COLLEG. B
  ];

  const lastRowMP = sheet.lastRow?.number || 1;
  for (let row = 2; row <= lastRowMP; row++) {
    const cell = sheet.getRow(row).getCell(1);
    if (cell.value && cell.value.toString().startsWith('#')) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFFE599' }
      };
    }
  }

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDBEAFE' }
    };
  });

  sheet.eachRow((row, rowNumber) => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' }, left: { style: 'thin' },
        bottom: { style: 'thin' }, right: { style: 'thin' }
      };
    });
    if (rowNumber > 1) {
      row.getCell(1).font = { bold: true };
    }
  });
};

const createGroupTitlesSheet = async (workbook, cablesToExport, getGroupTitle) => {
  const sheet = workbook.addWorksheet('Group Titles');

  sheet.addRow(['GRUPPO', 'TITOLO', 'NUM_CAVI']);

  const activeGroups = new Set();
  cablesToExport.forEach(cable => {
    if (cable.gruppo && cable.gruppo.trim() !== '') {
      activeGroups.add(cable.gruppo.trim());
    }
  });

  Array.from(activeGroups).sort((a, b) => {
    const numA = parseInt(a);
    const numB = parseInt(b);

    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
    if (!isNaN(numA) && isNaN(numB)) return -1;
    if (isNaN(numA) && !isNaN(numB)) return 1;

    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
  }).forEach(groupName => {
    const cablesInGroup = cablesToExport.filter(c => c.gruppo && c.gruppo.trim() === groupName);
    const title = getGroupTitle(groupName);

    sheet.addRow([
      groupName,
      title || '',
      cablesInGroup.length
    ]);
  });

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFDBEAFE' }
    };
  });

  sheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });
  });

  sheet.columns = [
    { width: 15 },
    { width: 40 },
    { width: 12 }
  ];
};

const createACPowerSheet = async (workbook, cablesToExport, settings, getSortedCablesForList, getCableDisplayName, getTerminalData, exportForEW3035, terminali, setWarnings) => {
  const sheet = workbook.addWorksheet('AC_Power');

  const headers = [
    'Num.', 'OrderNo.', 'Product', 'WireSize', 'TotalSet', 'T.Finish',
    'Length', 'Len.F1', 'Peel.F1', 'Len.B1', 'Peel.B1', 'Dia1', 'Back1',
    'ClampGapL', 'ClampGapR',
    '', '', '', '', '', '', '', '',  // 8 colonne vuote (P-W)
    'CharsSend', 'WordSize', 'BoldSize',
    'Head1Content', 'FlipH1', 'Head2Content', 'FlipH2', 'MidContent',
    'FlipM', 'Tail2Content', 'FlipT2', 'Tail1Content', 'FlipT1',
    'Printing', 'Head1PrintPos', 'Head2PrintPos', 'PrintLoopType',
    'PrintLoopNum', 'PrintLoopDist', 'Tail2PrintPos', 'Tail1PrintPos'
  ];
  sheet.addRow(headers);

  // Merge delle celle solo per le colonne con header
  for (let col = 1; col <= headers.length; col++) {
    if (headers[col - 1] !== '') {  // Solo se c'è un header
      const colLetter = sheet.getColumn(col).letter;
      sheet.mergeCells(`${colLetter}1:${colLetter}2`);
    }
  }

  const acPowerData = generateACPowerData(
    cablesToExport,
    settings,
    terminali, // Passa l'oggetto terminali corretto
    getSortedCablesForList,
    setWarnings, // Usa la funzione reale setWarnings
    exportForEW3035
  );

  acPowerData.forEach((row, index) => {
    if (exportForEW3035 && index > 0) {
      const prevRow = acPowerData[index - 1];
      const currentRow = row;

      if (prevRow.product !== currentRow.product || prevRow.wireSize !== currentRow.wireSize) {
        const emptyRow = new Array(headers.length).fill('');
        sheet.addRow(emptyRow);
      }
    }

    const rowData = [
      row.num, row.orderNo, row.product, row.wireSize, row.totalSet, row.tFinish,
      row.length, row.lenF1, row.peelF1, row.lenB1, row.peelB1, row.dia1, row.back1,
      row.clampGapL, row.clampGapR,
      row.empty1, row.empty2, row.empty3, row.empty4, row.empty5, row.empty6, row.empty7, row.empty8,
      row.charsSend, row.wordSize, row.boldSize,
      row.head1Content, row.flip1, row.head2Content, row.flip2, row.midContent,
      row.flip3, row.tail2Content, row.flip4, row.tail1Content, row.flip5,
      row.printing, row.head1PrintPos, row.head2PrintPos, row.printLoopType,
      row.printLoopNum, row.printLoopDist, row.tail2PrintPos, row.tail1PrintPos
    ];
    sheet.addRow(rowData);
  });

  sheet.getRow(1).eachCell(cell => {
    cell.font = { bold: true };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });

  sheet.eachRow(row => {
    row.eachCell(cell => {
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
  });

  // Imposta larghezza di default per tutte le colonne
  sheet.columns.forEach(col => col.width = 15);

  // Nascondi completamente le colonne vuote
  for (let i = 16; i <= 23; i++) {
    sheet.getColumn(i).hidden = true;
  }
};

// Funzione helper per generare il nome file completo
export const getFullFileName = (settings, extension = '', includeSort = true, includeQuantity = true, exportForEW3035) => {
  const cleanBaseName = settings.fileName.startsWith('ELECB') ? settings.fileName : `ELECB${settings.fileName}`;
  const withoutExtension = cleanBaseName.replace(/\.(xlsx?|pdf)$/i, '');

  const revisionFormatted = settings.revision.toString().padStart(2, '0');
  const revisionText = exportForEW3035
    ? `Rev${revisionFormatted}`      // Macchinetta: "Rev05"
    : `Rev. ${revisionFormatted}`;   // Normale: "Rev. 05"

  const fileParts = [cleanBaseName, revisionText];

  if (includeSort && settings.listSortMode !== 'numerazione') {
    const getSortModeDisplayName = (mode) => {
      switch (mode) {
        case 'siglatura': return 'Siglatura';
        case 'sezione': return 'Sezione';
        case 'coloreSezione': return 'Colore/Sezione';
        default: return mode;
      }
    };
    const sortDisplayName = getSortModeDisplayName(settings.listSortMode);
    if (sortDisplayName) {
      fileParts.push(sortDisplayName);
    }
  }

  if ((includeQuantity && settings.quantitaCablaggi > 1) || exportForEW3035) {
    const quantityText = exportForEW3035
      ? `Q${settings.quantitaCablaggi}`           // Macchinetta: sempre "Q1", "Q2", etc.
      : `#${settings.quantitaCablaggi}`;          // Normale: solo se > 1
    fileParts.push(quantityText);
  }

  const separator = exportForEW3035 ? '-' : ' - ';
  const fullName = fileParts.join(separator);

  return extension ? `${fullName}.${extension}` : fullName;
};